import type { AfterViewInit, OnDestroy, OnInit } from '@angular/core';
import { ChangeDetectorRef, Component, HostListener, inject, ViewChild } from '@angular/core';
import type {
  AbstractControl,
  UntypedFormControl,
  UntypedFormGroup,
  ValidatorFn,
} from '@angular/forms';
import { UntypedFormBuilder, Validators } from '@angular/forms';
import { ActivatedRoute, NavigationStart, Router } from '@angular/router';
import { isNullOrUndefined } from 'is-what';
import moment from 'moment-timezone';
import { finalize } from 'rxjs';
import { createNumberMask } from 'text-mask-addons/dist/textMaskAddons';

import type { IMyDateModel } from '../../common/legacy-lib/angular-mydatepicker/interfaces/my-date-model.interface';
import type { IAngularMyDpOptions } from '../../common/legacy-lib/angular-mydatepicker/interfaces/my-options.interface';
import type { CalculateFeeMegaResponse } from '../../mega-exchange/services/interfaces/calculate-fee-mega-response';
import { PassDataService } from '../../mega-exchange/services/pass-data.service';
import { StringConstants } from '../../rcr-exchange/constants/string-constants';
import type {
  Mega,
  MegaListPrefixResponse,
  Prefix,
} from '../../rcr-exchange/services/interfaces/mega-list-prefix-response';
import { ErrorHandlerService } from '../../shared/services/error-handler.service';
import { FmLoadingPageService } from '../../shared/services/fm-loading-page.service';
import { FmDatePickerMegaComponent } from '../fm-date-picker-mega/fm-date-picker-mega.component';
import type { CdxRequest } from '../services/interfaces/calculate-fee-mega-request';
import type { MegaErrors } from '../services/interfaces/mega-error';
import type { RetrieveMegaResponse } from '../services/interfaces/retrieve-mega-response';
import { MegaTradeNavigationService } from '../services/mega-trade-navigation.service';
import { MegasService } from '../services/megas.service';

@Component({
  standalone: false,
  selector: 'invd-enter-mega-trade-details',
  templateUrl: './enter-mega-trade-details-component.html',
  styleUrls: ['./enter-mega-trade-details-component.scss'],
})
export class EnterMegaTradeDetailsComponent implements OnInit, AfterViewInit, OnDestroy {
  public calendarModel: IMyDateModel = null;
  public dateSelected: any;

  public selectMegaTypePlaceholder = 'Select';
  public selectPrefixPlaceholder = 'Select a prefix';

  @ViewChild(FmDatePickerMegaComponent, { static: true })
  public FmDatePickerMegaComponent: FmDatePickerMegaComponent;

  public isNavigateToCommitPage = false;
  public displayNavigateAwayPopup = false;
  public displayNavigateAwayCancelPopup = false;
  public destinationUrl = '/';
  public isAllowToNavigateAway = false;

  public megaPrefixs: Mega[];
  public requestSingleFamilyFixMegaForm: UntypedFormGroup;
  public requestSingleFamilyArmMegaForm: UntypedFormGroup;
  public requestMultiFamilyFixMegaForm: UntypedFormGroup;
  public requestMultiFamilyArmMegaForm: UntypedFormGroup;

  public selectedMegas: Mega;
  public selectedSingleFamilyFixPrefix: Prefix = null;
  public sfFixCouponInput: number;
  public sfFixDealAmount: number;

  public singleFamilyArmSelectedPrefix: Prefix = null;
  public sfARMsubTypeInput: string;
  public sfArmDealAmount: number;

  public multiFamilyFixSelectedPrefix: Prefix = null;
  public mfFixHighCouponAmount: number;
  public mfFixLowCouponAmount: number;
  public mfFixDealAmount: number;

  public multiFamilyArmSelectedPrefix: Prefix = null;
  public mfARMsubTypeInput: string;
  public mfArmDealAmount: number;

  public showCalendarBusyIndicator = true;
  public isSingleFamilyFixSelected = false;
  public isSingleFamilyARMSelected = false;
  public isMultiFamilyFixSelected = false;
  public isMultiFamilyARMSelected = false;
  public calculateFeeMega: any;
  public subTypeVal: string;
  public warningMessage: any = { show: false, message: '' };
  public showSubTypeUpperCase = false;

  public settlementDate: string = null;
  public settlementMaxDate: string = null;
  public settlementDisableDates: Array<string> = null;
  public isDealAmtLessThanOneThousand = false;
  public numberMask: any;

  protected singleFamilyFixMegaSettlemntDateControlOptions: IAngularMyDpOptions = {
    dateFormat: 'mm/dd/yyyy',
  };
  protected singleFamilyArmMegaSettlemntDateControlOptions: IAngularMyDpOptions = {
    dateFormat: 'mm/dd/yyyy',
  };
  protected multiFamilyFixMegaSettlemntDateControlOptions: IAngularMyDpOptions = {
    dateFormat: 'mm/dd/yyyy',
  };
  protected multiFamilyArmMegaSettlemntDateControlOptions: IAngularMyDpOptions = {
    dateFormat: 'mm/dd/yyyy',
  };

  public isEditMegaFlow = false;
  public headerSubText: string;
  public poolNumber: string;
  public reviewButtonText: string;
  public isDisableMegaTypeSelection = false;
  public isMegaTypeDropdownOpen = false;
  private editMega: RetrieveMegaResponse;
  private calcFeeRequest: CdxRequest;
  public isReviewButtonClicked = false;

  private earlyClose: Array<any> = [];

  private router = inject(Router);
  private route = inject(ActivatedRoute);
  private fb = inject(UntypedFormBuilder);
  public fmLoadingPageService = inject(FmLoadingPageService);
  private errorHandlerService = inject(ErrorHandlerService);
  private passDataService = inject(PassDataService);
  private megaNavService = inject(MegaTradeNavigationService);
  private megaService = inject(MegasService);
  private readonly cdr = inject(ChangeDetectorRef);

  @HostListener('window:beforeunload', ['$event'])
  refreshBtnClick($event) {
    $event.returnValue = true;
  }

  ngOnInit() {
    this.navigationController();

    if (this.passDataService.getRetrieveMegaResponse() != null) {
      this.isEditMegaFlow = true;
      this.editMega = this.passDataService.getRetrieveMegaResponse();
      this.poolNumber = this.editMega.poolNumber.toString();
      this.headerSubText =
        StringConstants.MEGA_ENTER_DETAILS_SCREEN_SUB_HEADER_EDIT + ' (' + this.poolNumber + ')';
      this.reviewButtonText = StringConstants.MEGA_ENTER_DETAILS_SCREEN_REVIEW_BUTTON_EDIT_TEXT;
      this.dateSelected = moment(this.editMega.tradeSettlementDate);
    } else if (this.passDataService.getEditMegaFlow()) {
      this.isEditMegaFlow = true;
      this.poolNumber = this.passDataService.getpoolNumberEditMegaFlow().toString();
      this.headerSubText =
        StringConstants.MEGA_ENTER_DETAILS_SCREEN_SUB_HEADER_EDIT + ' (' + this.poolNumber + ')';
      this.reviewButtonText = StringConstants.MEGA_ENTER_DETAILS_SCREEN_REVIEW_BUTTON_EDIT_TEXT;
    } else {
      this.isEditMegaFlow = false;
      this.headerSubText = StringConstants.MEGA_ENTER_DETAILS_SCREEN_SUB_HEADER;
      this.reviewButtonText = StringConstants.MEGA_ENTER_DETAILS_SCREEN_REVIEW_BUTTON_TEXT;
    }

    this.requestSingleFamilyFixMegaForm = this.fb.group({
      singleFamilyFixPrefixSelControl: [null, Validators.required],
      singleFamilyFixCouponInputControl: [
        'enter coupon',
        Validators.compose([Validators.required, Validators.minLength(1)]),
      ],
      singleFamilyFixMegaAmountInputControl: [
        'enter deal amount',
        Validators.compose([Validators.required, this.validateNumber()]),
      ],
      singleFamilyFixMegaSettlemntDateControl: [null, Validators.required],
    });

    this.requestSingleFamilyArmMegaForm = this.fb.group({
      singleFamilyArmPrefixSelControl: [null, Validators.required],
      singleFamilyArmSubTypeInputControl: [
        'enter subtype',
        Validators.compose([Validators.required, this.subTypeValidator()]),
      ],
      singleFamilyArmMegaAmountInputControl: [
        'enter deal amount',
        Validators.compose([Validators.required, this.validateNumber()]),
      ],
      singleFamilyArmMegaSettlemntDateControl: [null, Validators.required],
    });

    this.requestMultiFamilyFixMegaForm = this.fb.group({
      multiFamilyFixPrefixSelControl: [null, Validators.required],
      megaHighCouponInputControl: [
        'enter high coupon',
        Validators.compose([Validators.required, this.couponValidator()]),
      ],
      megaLowCouponInputControl: [
        'enter low coupon',
        Validators.compose([Validators.required, this.couponValidator()]),
      ],
      multiFamilyFixMegaAmountInputControl: [
        'enter deal amount',
        Validators.compose([Validators.required, this.validateNumber()]),
      ],
      multiFamilyFixMegaSettlemntDateControl: [null, Validators.required],
    });

    this.requestMultiFamilyArmMegaForm = this.fb.group({
      multiFamilyArmPrefixSelControl: [null, Validators.required],
      multiFamilyArmSubTypeInputControl: [
        'enter subtype',
        Validators.compose([Validators.required, this.subTypeValidator()]),
      ],
      multiFamilyArmMegaAmountInputControl: [
        'enter deal amount',
        Validators.compose([Validators.required, this.validateNumber()]),
      ],
      multiFamilyArmMegaSettlemntDateControl: [null, Validators.required],
    });

    this.getListPrefix();

    this.numberMask = createNumberMask({
      prefix: '',
    });
  }

  ngOnDestroy() {
    if (this.passDataService != null) {
      this.passDataService.setSettledMegaRecreateMegaType('');
      this.passDataService.setRetrieveMegaResponse(null);
      if (this.passDataService.getMegaRepopulateBundle() != null) {
        this.passDataService.setEditMegaFlow(this.isEditMegaFlow);
      } else {
        this.passDataService.setEditMegaFlow(false);
      }
    }
  }

  showWarningMessage(show, message): void {
    this.warningMessage = { show, message };
  }

  canDeactivate(): Promise<boolean> | boolean {
    if (!this.isNavigateToCommitPage) {
      this.passDataService.setMegaRepopulateBundle(null);
    }
    return this.isAllowToNavigateAway;
  }

  public setSelectedMega() {
    this.isSingleFamilyFixSelected =
      this.selectedMegas.megaType === StringConstants.MEGA_TYPE_SINGLE_FAMILY_FIX;
    this.isSingleFamilyARMSelected =
      this.selectedMegas.megaType === StringConstants.MEGA_TYPE_SINGLE_FAMILY_ARM;
    this.isMultiFamilyFixSelected =
      this.selectedMegas.megaType === StringConstants.MEGA_TYPE_MULTI_FAMILY_FIX;
    this.isMultiFamilyARMSelected =
      this.selectedMegas.megaType === StringConstants.MEGA_TYPE_MULTI_FAMILY_ARM;
  }

  private repopulateSelectedMegaInfoFromReviewAndCommitPage() {
    const megaType: string = this.passDataService.getMegaRepopulateBundle().megaType;
    this.megaPrefixs.forEach((item) => {
      if (megaType === item.megaType) {
        this.selectedMegas = item;
        if (this.selectedMegas != null) {
          this.setSelectedMegaType(this.selectedMegas);
          this.setSelectedMega();

          if (this.isEditMegaFlow) {
            this.isDisableMegaTypeSelection = true;
          } else {
            this.isDisableMegaTypeSelection = false;
          }

          this.dateSelected = moment(
            this.passDataService.getMegaRepopulateBundle().tradeSettlementDate,
          );
          this.calendarModel = {
            isRange: false,
            singleDate: {
              date: {
                year: this.dateSelected.year(),
                month: this.dateSelected.month() + 1,
                day: this.dateSelected.date(),
              },
            },
          };

          if (this.isSingleFamilyFixSelected) {
            this.selectedMegas.prefixTypes.forEach((type) => {
              if (this.passDataService.getMegaRepopulateBundle().prefixCode === type.prefixCode) {
                this.setSingleFamilyFixSelectedPrefix(type);
              }
            });
            this.sfFixCouponInput = this.passDataService.getMegaRepopulateBundle().sfFixedCoupon;
            this.sfFixDealAmount = this.passDataService.getMegaRepopulateBundle().tradeAmount;
          }
          if (this.isSingleFamilyARMSelected) {
            this.selectedMegas.prefixTypes.forEach((type) => {
              if (this.passDataService.getMegaRepopulateBundle().prefixCode === type.prefixCode) {
                this.singleFamilyArmSetSelectedPrefix(type);
              }
            });
            this.sfARMsubTypeInput = this.passDataService.getMegaRepopulateBundle().sfARMSubType;
            this.sfArmDealAmount = this.passDataService.getMegaRepopulateBundle().tradeAmount;
          }
          if (this.isMultiFamilyFixSelected) {
            this.selectedMegas.prefixTypes.forEach((type) => {
              if (this.passDataService.getMegaRepopulateBundle().prefixCode === type.prefixCode) {
                this.multiFamilyFixSetSelectedPrefix(type);
              }
            });
            this.mfFixHighCouponAmount =
              this.passDataService.getMegaRepopulateBundle().mfHighCoupon;
            this.mfFixLowCouponAmount = this.passDataService.getMegaRepopulateBundle().mfLowCoupon;
            this.mfFixDealAmount = this.passDataService.getMegaRepopulateBundle().tradeAmount;
          }
          if (this.isMultiFamilyARMSelected) {
            this.selectedMegas.prefixTypes.forEach((type) => {
              if (this.passDataService.getMegaRepopulateBundle().prefixCode === type.prefixCode) {
                this.multiFamilyArmSetSelectedPrefix(type);
              }
            });
            this.mfARMsubTypeInput = this.passDataService.getMegaRepopulateBundle().sfARMSubType;
            this.mfArmDealAmount = this.passDataService.getMegaRepopulateBundle().tradeAmount;
          }
        }
      }
    });
  }

  public repopulateSelectedMegaForEditMegaFlowFromCommitedMegaScreen() {
    const megaType: string = this.editMega.megaType;
    this.megaPrefixs.forEach((item) => {
      if (megaType === item.megaType) {
        this.selectedMegas = item;
        if (this.selectedMegas != null) {
          this.setSelectedMegaType(this.selectedMegas);
          this.setSelectedMega();

          this.isDisableMegaTypeSelection = true;

          this.dateSelected = moment(this.editMega.tradeSettlementDate);
          this.calendarModel = {
            isRange: false,
            singleDate: {
              date: {
                year: this.dateSelected.year(),
                month: this.dateSelected.month() + 1,
                day: this.dateSelected.date(),
              },
            },
          };

          if (this.isSingleFamilyFixSelected) {
            this.selectedMegas.prefixTypes.forEach((type) => {
              if (this.editMega.prefixCode === type.prefixCode) {
                this.setSingleFamilyFixSelectedPrefix(type);
              }
            });
            this.sfFixCouponInput = this.editMega.sfFixedCoupon;
            this.sfFixDealAmount = this.editMega.tradeAmount;
          }
          if (this.isSingleFamilyARMSelected) {
            this.selectedMegas.prefixTypes.forEach((type) => {
              if (this.editMega.prefixCode === type.prefixCode) {
                this.singleFamilyArmSetSelectedPrefix(type);
              }
            });
            this.sfARMsubTypeInput = this.editMega.sfARMSubType;
            this.sfArmDealAmount = this.editMega.tradeAmount;
          }
          if (this.isMultiFamilyFixSelected) {
            this.selectedMegas.prefixTypes.forEach((type) => {
              if (this.editMega.prefixCode === type.prefixCode) {
                this.multiFamilyFixSetSelectedPrefix(type);
              }
            });
            this.mfFixHighCouponAmount = this.editMega.mfHighCoupon;
            this.mfFixLowCouponAmount = this.editMega.mfLowCoupon;
            this.mfFixDealAmount = this.editMega.tradeAmount;
          }
          if (this.isMultiFamilyARMSelected) {
            this.selectedMegas.prefixTypes.forEach((type) => {
              if (this.editMega.prefixCode === type.prefixCode) {
                this.multiFamilyArmSetSelectedPrefix(type);
              }
            });
            this.mfARMsubTypeInput = this.editMega.sfARMSubType;
            this.mfArmDealAmount = this.editMega.tradeAmount;
          }
        }
      }
    });
  }

  isValidDateSelecte(dateObject: IMyDateModel): boolean {
    return dateObject.hasOwnProperty('singleDate');
  }

  getDatePickerFormatedDate(dateString): any {
    const newDate = new Date(dateString);
    return {
      date: { year: newDate.getFullYear(), month: newDate.getMonth() + 1, day: newDate.getDate() },
      formatted: newDate.toDateString(),
    };
  }

  ngAfterViewInit() {
    this.fmLoadingPageService.isLoaderRequesting.set(true);
    const megaTradeFormInfo: HTMLElement = document.getElementsByClassName(
      'mega-trade-form-info',
    )[0] as HTMLElement;
    const megaTradeForm: HTMLElement = document.getElementsByClassName(
      'mega-trade-form',
    )[0] as HTMLElement;
    megaTradeForm.style.minHeight = megaTradeFormInfo.offsetHeight + 'px';
    this.onInitTooltipError();
  }

  private navigationController() {
    this.router.events.subscribe((event) => {
      if (event instanceof NavigationStart) {
        if (event.url.indexOf('review-and-commit') > 0) {
          if (this.isEditMegaFlow) {
            this.passDataService.setEditMegaFlow(this.isEditMegaFlow);
            if (this.editMega != null) {
              this.passDataService.setCUSIPEditMegaFlow(this.editMega.cusip);
              this.passDataService.setpoolNumberEditMegaFlow(this.editMega.poolNumber);
              this.passDataService.setRevisionNumber(this.editMega.tradeRevisionNumber);
            }
          } else {
            this.passDataService.setEditMegaFlow(this.isEditMegaFlow);
          }
          this.isNavigateToCommitPage = true;
          this.isAllowToNavigateAway = true;
        } else if (!event.url.includes('enter-details') && this.selectedMegas) {
          if (event.url.indexOf('error') > 0) {
            this.displayNavigateAwayPopup = false;
            this.isAllowToNavigateAway = true;
            this.destinationUrl = event.url;
          } else {
            this.displayNavigateAwayPopup = true;
            this.isAllowToNavigateAway = false;
            this.destinationUrl = event.url;
          }
        } else {
          this.displayNavigateAwayPopup = false;
          this.isAllowToNavigateAway = true;
          this.isNavigateToCommitPage = false;
        }
      }
    });
  }

  public navigatePageTo(pageURL) {
    this.router.navigate([pageURL]);
    this.isAllowToNavigateAway = true;
  }

  public toggleMegaTypeDropdown() {
    this.isMegaTypeDropdownOpen = !this.isMegaTypeDropdownOpen;
  }

  public closeMegaTypeDropdown() {
    this.isMegaTypeDropdownOpen = false;
  }

  public setSelectedMegaType(mselectedMegaType: Mega) {
    this.isMegaTypeDropdownOpen = false;
    this.selectedMegas = mselectedMegaType;
    if (this.selectedMegas != null) {
      this.setSelectedMega();
      this.requestSingleFamilyFixMegaForm.reset();
      this.requestSingleFamilyArmMegaForm.reset();
      this.requestMultiFamilyFixMegaForm.reset();
      this.requestMultiFamilyArmMegaForm.reset();
      this.isDealAmtLessThanOneThousand = false;
    }

    this.getSettlementDate(mselectedMegaType.megaType, this.poolNumber);
    this.showWarningMessage(false, '');
  }

  public setSingleFamilyFixSelectedPrefix(mSelPrefix: Prefix) {
    this.selectedSingleFamilyFixPrefix = mSelPrefix;
  }

  public singleFamilyArmSetSelectedPrefix(mSelPrefix: Prefix) {
    this.singleFamilyArmSelectedPrefix = mSelPrefix;
  }

  public multiFamilyFixSetSelectedPrefix(mSelPrefix: Prefix) {
    this.multiFamilyFixSelectedPrefix = mSelPrefix;
  }

  public multiFamilyArmSetSelectedPrefix(mSelPrefix: Prefix) {
    this.multiFamilyArmSelectedPrefix = mSelPrefix;
  }

  public formatCouponDecimal(event: Event, currentControl: AbstractControl): void {
    const inputField = currentControl.value;
    if (!isNaN(inputField) && inputField !== '' && !isNullOrUndefined(inputField)) {
      const mNumber: number = parseFloat(inputField.toString());
      const formatValue: string = mNumber.toFixed(3);
      currentControl.setValue(formatValue);
    }
  }

  private validateNumber(): ValidatorFn {
    return (control: AbstractControl): { [key: string]: boolean } => {
      if (control.value < 1000) {
        // eslint-disable-next-line @typescript-eslint/naming-convention
        return { DealAmount_less_than_one_thousand: false };
      } else {
        return null;
      }
    };
  }

  public validateDealAmount(ctl: AbstractControl) {
    if (ctl.value < 1000) {
      this.isDealAmtLessThanOneThousand = true;
    } else {
      this.isDealAmtLessThanOneThousand = false;
    }
  }

  private couponValidator(): ValidatorFn {
    function isEmptyInputValue(value: any): boolean {
      if (isNullOrUndefined(value) || isNaN(value) || value.length === 0) {
        return true;
      } else {
        return false;
      }
    }

    return (control: AbstractControl): { [key: string]: boolean } => {
      if (
        isEmptyInputValue(this.mfFixLowCouponAmount) ||
        isEmptyInputValue(this.mfFixHighCouponAmount)
      ) {
        // eslint-disable-next-line @typescript-eslint/naming-convention
        return { CouponError_one: false, CouponError_two: false };
      }

      if (control.dirty) {
        if (this.mfFixLowCouponAmount - this.mfFixHighCouponAmount > 0) {
          // eslint-disable-next-line @typescript-eslint/naming-convention
          return { CouponError_one: true, CouponError_two: false };
        } else if (this.mfFixHighCouponAmount - this.mfFixLowCouponAmount > 1) {
          // eslint-disable-next-line @typescript-eslint/naming-convention
          return { CouponError_one: false, CouponError_two: true };
        } else {
          return null;
        }
      }
    };
  }

  public subTypeValidator(): ValidatorFn {
    function isEmptyInputValue(value: any): boolean {
      if (isNullOrUndefined(value) || value.length === 0) {
        return true;
      } else if (value.length < 2) {
        return false;
      }
    }
    return (control: UntypedFormControl): { [key: string]: boolean } => {
      if (this.sfARMsubTypeInput && isEmptyInputValue(this.sfARMsubTypeInput)) {
        this.showSubTypeUpperCase = false;
        return null;
      }

      if (this.mfARMsubTypeInput && isEmptyInputValue(this.mfARMsubTypeInput)) {
        this.showSubTypeUpperCase = false;
        return null;
      }

      if (control.dirty) {
        this.showSubTypeUpperCase = true;
        if (control && control.value && control.value.length < 2) {
          return { subTypeError: true };
        }
      }
    };
  }

  public reviewMega(): void {
    this.isReviewButtonClicked = true;
    this.fmLoadingPageService.isLoaderRequesting.set(true);
    this.getCalculateFee();
  }

  public cancelMega(): void {
    this.displayNavigateAwayCancelPopup = true;
    this.destinationUrl = '/mega-trade/committed-mega';
  }

  public highCouponValueChanged() {
    this.requestMultiFamilyFixMegaForm.get('megaLowCouponInputControl').updateValueAndValidity();
  }

  public lowCouponValueChanged() {
    this.requestMultiFamilyFixMegaForm.get('megaHighCouponInputControl').updateValueAndValidity();
  }

  private onInitTooltipError(): void {}

  public getListPrefix(): void {
    const listPrefixRequest: any = {};

    if (this.isEditMegaFlow) {
      listPrefixRequest.poolNumber = this.poolNumber;
    } else {
      listPrefixRequest.poolNumber = null;
    }

    this.megaService.listPrefix(listPrefixRequest).subscribe({
      next: (data) => this.handelListPrefixServiceSuccess(data as MegaListPrefixResponse),
      error: (error) => this.handleAWSListPrefifxServiceFail(error),
    });
  }

  private getSettlementDate(megaType: string, _poolNumber: string): void {
    this.showCalendarBusyIndicator = true;
    this.cdr.markForCheck();
    const settleDateInfoMegaRequest: any = {};
    settleDateInfoMegaRequest.megaType = megaType;
    settleDateInfoMegaRequest.isCollateralAnalyzer = false;

    if (this.isEditMegaFlow) {
      settleDateInfoMegaRequest.poolNumber = this.poolNumber;
    } else {
      settleDateInfoMegaRequest.poolNumber = null;
    }

    this.megaService
      .settlementDate(settleDateInfoMegaRequest)
      .pipe(finalize(() => this.clearCalendarBusyIndicator()))
      .subscribe({
        next: (data) => this.handelSettlementDateServiceSuccess(data as any),
        error: (error) => this.handleSettlementDateServiceFail(error),
      });
  }

  private handelSettlementDateServiceSuccess(settlementDate: any) {
    const startDateArray = settlementDate.startDate.split('-');
    const startDate = new Date(startDateArray[0], Number(startDateArray[1]) - 1, startDateArray[2]);
    if (settlementDate.hasOwnProperty('earlyClose')) {
      this.earlyClose = settlementDate.earlyClose;
      this.earlyClose = this.removeDateTimestamp(this.earlyClose);
    }

    const endDateArray = settlementDate.endDate.split('-');
    const endDate = new Date(
      endDateArray[0],
      Number(endDateArray[1]) - 1,
      Number(endDateArray[2]) + 1,
    );

    if (this.isSingleFamilyFixSelected)
      this.singleFamilyFixDates(startDate, endDate, settlementDate.exclusions);
    if (this.isSingleFamilyARMSelected)
      this.singleFamilyArmDates(startDate, endDate, settlementDate.exclusions);
    if (this.isMultiFamilyFixSelected)
      this.multiFamilyFixDates(startDate, endDate, settlementDate.exclusions);
    if (this.isMultiFamilyARMSelected)
      this.multiFamilyArmDates(startDate, endDate, settlementDate.exclusions);
  }

  protected canIShowCalenderToolTip(selectedDate: any): boolean {
    if (selectedDate && selectedDate.hasOwnProperty('date')) {
      const monthVal = ('0' + selectedDate.date.month).slice(-2);
      const dayVal = ('0' + selectedDate.date.day).slice(-2);
      const tempDate = selectedDate.date.year + '-' + monthVal + '-' + dayVal;
      return this.earlyClose.includes(tempDate);
    }
    return false;
  }

  private singleFamilyFixDates(sDate: Date, eDate: Date, exclusions: Array<string>): void {
    this.singleFamilyFixMegaSettlemntDateControlOptions = {
      firstDayOfWeek: 'su',
      satHighlight: false,
      sunHighlight: false,
      disableWeekends: true,
      disableUntil: {
        year: sDate.getFullYear(),
        month: sDate.getMonth() + 1,
        day: sDate.getDate(),
      },
      disableSince: {
        year: eDate.getFullYear(),
        month: eDate.getMonth() + 1,
        day: eDate.getDate(),
      },
      disableDates: this.singleFamilyFixDatesExclusionDates(exclusions),
    };
  }

  private singleFamilyArmDates(sDate: Date, eDate: Date, exclusions: Array<string>): void {
    this.singleFamilyArmMegaSettlemntDateControlOptions = {
      firstDayOfWeek: 'su',
      satHighlight: false,
      sunHighlight: false,
      disableWeekends: true,
      disableUntil: {
        year: sDate.getFullYear(),
        month: sDate.getMonth() + 1,
        day: sDate.getDate(),
      },
      disableSince: {
        year: eDate.getFullYear(),
        month: eDate.getMonth() + 1,
        day: eDate.getDate(),
      },
      disableDates: this.singleFamilyFixDatesExclusionDates(exclusions),
    };
  }

  private multiFamilyFixDates(sDate: Date, eDate: Date, exclusions: Array<string>): void {
    this.multiFamilyFixMegaSettlemntDateControlOptions = {
      firstDayOfWeek: 'su',
      satHighlight: false,
      sunHighlight: false,
      disableWeekends: true,
      disableUntil: {
        year: sDate.getFullYear(),
        month: sDate.getMonth() + 1,
        day: sDate.getDate(),
      },
      disableSince: {
        year: eDate.getFullYear(),
        month: eDate.getMonth() + 1,
        day: eDate.getDate(),
      },
      disableDates: this.singleFamilyFixDatesExclusionDates(exclusions),
    };
  }

  private multiFamilyArmDates(sDate: Date, eDate: Date, exclusions: Array<string>): void {
    this.multiFamilyArmMegaSettlemntDateControlOptions = {
      firstDayOfWeek: 'su',
      satHighlight: false,
      sunHighlight: false,
      disableWeekends: true,
      disableUntil: {
        year: sDate.getFullYear(),
        month: sDate.getMonth() + 1,
        day: sDate.getDate(),
      },
      disableSince: {
        year: eDate.getFullYear(),
        month: eDate.getMonth() + 1,
        day: eDate.getDate(),
      },
      disableDates: this.singleFamilyFixDatesExclusionDates(exclusions),
    };
  }

  private singleFamilyFixDatesExclusionDates(dateExclusions: Array<string>): Array<any> {
    const exclusionDates: Array<any> = new Array<any>();

    dateExclusions = this.removeDateTimestamp(dateExclusions);

    for (const dateExclusion of dateExclusions) {
      const convertedDateArray = dateExclusion.split('-');
      const convertedDate = new Date(
        Number(convertedDateArray[0]),
        Number(convertedDateArray[1]) - 1,
        Number(convertedDateArray[2]),
      );
      exclusionDates.push({
        year: convertedDate.getFullYear(),
        month: convertedDate.getMonth() + 1,
        day: convertedDate.getDate(),
      });
    }

    return exclusionDates;
  }

  private handleSettlementDateServiceFail(_error: any): void {}

  private clearCalendarBusyIndicator(): void {
    this.showCalendarBusyIndicator = false;
    this.cdr.markForCheck();
  }

  private clearSeparators(inputNumber: string): string {
    return inputNumber.replace(/,/g, '');
  }

  private getCalculateFee(): void {
    const mBundle = {} as CdxRequest;
    mBundle.megaType = this.selectedMegas.megaType;
    mBundle.prefixCode = this.selectedSingleFamilyFixPrefix
      ? this.selectedSingleFamilyFixPrefix.prefixCode
      : this.singleFamilyArmSelectedPrefix
        ? this.singleFamilyArmSelectedPrefix.prefixCode
        : this.multiFamilyFixSelectedPrefix
          ? this.multiFamilyFixSelectedPrefix.prefixCode
          : '';
    this.dateSelected = moment(this.dateSelected).format('YYYY-MM-DD').toString();
    mBundle.tradeSettlementDate = this.dateSelected;
    if (this.isSingleFamilyFixSelected) {
      mBundle.tradeAmount = Number(this.clearSeparators(this.sfFixDealAmount.toString()));
      mBundle.sfFixedCoupon = this.sfFixCouponInput;
    }
    if (this.isSingleFamilyARMSelected) {
      mBundle.tradeAmount = Number(this.clearSeparators(this.sfArmDealAmount.toString()));
      mBundle.sfARMSubType = this.sfARMsubTypeInput && this.sfARMsubTypeInput.toUpperCase();
    }
    if (this.isMultiFamilyFixSelected) {
      mBundle.tradeAmount = Number(this.clearSeparators(this.mfFixDealAmount.toString()));
      mBundle.mfHighCoupon = this.mfFixHighCouponAmount;
      mBundle.mfLowCoupon = this.mfFixLowCouponAmount;
    }

    if (this.isMultiFamilyARMSelected) {
      mBundle.prefixCode = this.multiFamilyArmSelectedPrefix
        ? this.multiFamilyArmSelectedPrefix.prefixCode
        : '';
      mBundle.tradeAmount = Number(this.clearSeparators(this.mfArmDealAmount.toString()));
      mBundle.sfARMSubType = this.mfARMsubTypeInput && this.mfARMsubTypeInput.toUpperCase();
    }

    if (this.isEditMegaFlow) {
      mBundle.poolNumber = this.poolNumber;
    } else {
      mBundle.poolNumber = null;
    }

    this.calcFeeRequest = mBundle;

    this.megaService.calculateFee(this.calcFeeRequest).subscribe({
      next: (data) => this.handelCalculateFeeServiceSuccess(data as CalculateFeeMegaResponse),
      error: (error) => this.handleAWSCalculateFeeServiceFail(error),
    });
  }

  private handelCalculateFeeServiceSuccess(calcFee: CalculateFeeMegaResponse) {
    this.fmLoadingPageService.stopLoading();
    this.passDataService.setMegaRepopulateBundle(this.calcFeeRequest);
    this.passDataService.setCalculateFeeMegaResponse(calcFee);
    this.passDataService.setMegaType(this.selectedMegas.megaType);
    this.megaNavService.canNavigate = true;
    this.router.navigate(['mega-trade/enter-details/review-and-commit']);
  }

  public handelListPrefixServiceSuccess(listPrefixs: MegaListPrefixResponse) {
    this.fmLoadingPageService.isLoaderRequesting.set(false);
    this.megaPrefixs = listPrefixs.megaPrefixes;

    if (this.passDataService.getMegaRepopulateBundle() != null) {
      this.repopulateSelectedMegaInfoFromReviewAndCommitPage();
    }

    if (this.passDataService.getRetrieveMegaResponse() != null) {
      this.repopulateSelectedMegaForEditMegaFlowFromCommitedMegaScreen();
    }

    if (this.passDataService != null) {
      if (
        this.passDataService.getSettledMegaRecreateMegaType() ===
        StringConstants.MEGA_TYPE_SINGLE_FAMILY_FIX
      ) {
        listPrefixs.megaPrefixes.forEach((item) => {
          if (item.megaType === StringConstants.MEGA_TYPE_SINGLE_FAMILY_FIX) {
            this.setSelectedMegaType(item);
          }
        });
      } else if (
        this.passDataService.getSettledMegaRecreateMegaType() ===
        StringConstants.MEGA_TYPE_SINGLE_FAMILY_ARM
      ) {
        listPrefixs.megaPrefixes.forEach((item) => {
          if (item.megaType === StringConstants.MEGA_TYPE_SINGLE_FAMILY_ARM) {
            this.setSelectedMegaType(item);
          }
        });
      } else if (
        this.passDataService.getSettledMegaRecreateMegaType() ===
        StringConstants.MEGA_TYPE_MULTI_FAMILY_FIX
      ) {
        listPrefixs.megaPrefixes.forEach((item) => {
          if (item.megaType === StringConstants.MEGA_TYPE_MULTI_FAMILY_FIX) {
            this.setSelectedMegaType(item);
          }
        });
      } else if (
        this.passDataService.getSettledMegaRecreateMegaType() ===
        StringConstants.MEGA_TYPE_MULTI_FAMILY_ARM
      ) {
        listPrefixs.megaPrefixes.forEach((item) => {
          if (item.megaType === StringConstants.MEGA_TYPE_MULTI_FAMILY_ARM) {
            this.setSelectedMegaType(item);
          }
        });
      }
    }
  }

  public handleAWSListPrefifxServiceFail(_error: any): void {
    this.fmLoadingPageService.isLoaderRequesting.set(false);
  }

  public handleAWSCalculateFeeServiceFail(error: any): void {
    this.fmLoadingPageService.isLoaderRequesting.set(false);
    const errorObject: MegaErrors = error.error;
    this.showWarningMessage(true, errorObject.messages[0].errorMessage);
  }

  public removeDateTimestamp(dateArray: any): any {
    const dateArrayTemp = [];
    dateArray.forEach((element) => {
      let tempDate = '';
      tempDate = element && element.split('T')[0];
      dateArrayTemp.push(tempDate);
    });
    return dateArrayTemp;
  }

  public onDateChanged(date: IMyDateModel) {
    this.dateSelected = date.singleDate.jsDate;
  }

  protected trackByIdentity(_index: number, obj: any) {
    return obj;
  }
}
